import { Button, formatMoney } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { RadioCards } from "@majitap/ui/arc/radio-cards/radio-cards";
import SegmentedControl from "@majitap/ui/arc/segmented-control/segmented-control";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, errorOf } from "../../api";
import { useCompanyFormat } from "../../company";
import { type BoardDelivery, dispatchActions, rerouteOptionsQuery } from "../../dispatch";
import { messageFor } from "../../error-copy";
import { useFormCheck } from "../../form-check";
import { ChoiceField } from "../shared/choice-field";
import { ReasonFields, useReasonPicker } from "../shared/reason-fields";
import { useRefreshBoard } from "./actions";
import styles from "./dispatch.module.css";

type Mode = "place" | "client";
type Pending = { id: string; delivery_number: string };
/** What a reroute left: the first client's new delivery (if any), and the delivery that took the load. */
type Outcome = { replacement: Pending | null; target: Pending | null };

/**
 * Reroute a delivery at any open stage (`Console / Dispatch / In progress / Reroute`, P8-50,
 * P8-65): to another place of the same customer, re-priced there, or, once the truck has set off
 * with it, its load to another client's waiting delivery of the same items, which takes the truck
 * and goes on its way. Once the truck has set off, the customer is charged the reroute fee and the
 * truck's owner is paid, as Pricing sets them; before that nothing is charged.
 */
export function RerouteDialog({ delivery, onClose }: { delivery: BoardDelivery; onClose: () => void }) {
  const refresh = useRefreshBoard();
  const format = useCompanyFormat();
  const [mode, setMode] = useState<Mode>("place");
  const [locationId, setLocationId] = useState<string | null>(null);
  const [targetId, setTargetId] = useState<string | null>(null);
  const [firstClient, setFirstClient] = useState<"deliver_later" | "cancel">("deliver_later");
  const reason = useReasonPicker("delivery_rerouted");
  const check = useFormCheck();
  const options = useQuery(rerouteOptionsQuery(delivery.id));
  const places = useQuery({
    queryKey: ["reroute-places", delivery.order.id],
    queryFn: async () => {
      const { data: order, error } = await api.GET("/v1/admin/orders/{id}", {
        params: { path: { id: delivery.order.id } },
      });
      if (error || !order) throw errorOf(error);
      const owner = order.business ? { business_id: order.business.id } : { customer_id: order.customer.id };
      const { data, error: e2 } = await api.GET("/v1/admin/locations", { params: { query: { ...owner, limit: 100 } } });
      if (e2 || !data) throw errorOf(e2);
      return data.data.filter((l) => l.id !== delivery.location?.id);
    },
  });
  const mutation = useMutation({
    mutationFn: (): Promise<Outcome> =>
      mode === "place"
        ? dispatchActions
            .reroute(delivery.id, locationId ?? "", reason.text, reason.reasonId)
            .then((r) => ({ replacement: r?.replacement ?? null, target: null }))
        : dispatchActions
            .rerouteToDelivery(delivery.id, {
              target_delivery_id: targetId ?? "",
              first_client: firstClient,
              reason: reason.text,
              reason_id: reason.reasonId,
            })
            .then((r) => ({ replacement: r?.replacement ?? null, target: r?.target ?? null })),
    onSuccess: () => refresh(),
  });

  if (mutation.data) {
    const { target, replacement } = mutation.data;
    const description = target
      ? `${target.delivery_number} has the truck and is on its way. ${delivery.delivery_number} is now Rerouted. ${
          replacement
            ? `${delivery.order.customer_name}'s new delivery, ${replacement.delivery_number}, waits under Unassigned.`
            : `${delivery.order.customer_name} gets no new delivery.`
        }`
      : `${delivery.delivery_number} is now Rerouted. The new delivery, ${replacement?.delivery_number}, waits under Unassigned: plan it as usual, and give the customer and driver its number.`;
    return (
      <Dialog open onOpenChange={(open) => !open && onClose()}>
        <DialogContent
          title={target ? `Load sent to ${target.delivery_number}` : `Rerouted to ${replacement?.delivery_number}`}
          description={description}
        >
          <div className={styles.footer}>
            <Button onClick={onClose}>Done</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const o = options.data;
  // another client can take the load only once it is on board
  const canSendLoad = o?.on_the_road ?? false;
  const errors = {
    reason: reason.missing,
    place: mode === "place" && !locationId ? "Choose the place it goes to." : undefined,
    target: mode === "client" && !targetId ? "Choose the delivery that takes the load." : undefined,
  };
  // nothing to choose from yet (still loading), or nothing at all (an alert says so)
  const noChoice =
    mode === "place"
      ? places.data === undefined || places.data.length === 0
      : o === undefined || o.targets.length === 0;
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        title={`Reroute ${delivery.delivery_number}`}
        description={
          mode === "place"
            ? "A new delivery goes to their other place, priced there. This one becomes Rerouted."
            : "Another client's waiting delivery of the same load takes the truck and goes on its way. This one becomes Rerouted."
        }
      >
        <div className={styles.form}>
          {canSendLoad ? (
            <ChoiceField label="Send it to">
              <SegmentedControl
                label="Send it to"
                value={mode}
                onValueChange={(v) => setMode(v as Mode)}
                options={[
                  { value: "place", label: "Their other place" },
                  { value: "client", label: "Another client" },
                ]}
              />
            </ChoiceField>
          ) : o ? (
            <p className={styles.muted}>
              No truck has set off with the load yet, so nothing is charged. Any fill site, truck and driver come off
              this one.
            </p>
          ) : null}
          {mode === "place" ? (
            places.data?.length === 0 ? (
              <Alert tone="warning" title="No other place">
                The customer has no other place to send it to. Add one on their page first.
              </Alert>
            ) : (
              <ChoiceField label="New place">
                <RadioCards
                  layout="list"
                  aria-label="New place"
                  value={locationId}
                  onValueChange={setLocationId}
                  options={(places.data ?? []).map((l) => ({
                    value: l.id,
                    label: l.label,
                    description: l.address_line ?? undefined,
                  }))}
                />
                {check.show(errors.place) ? (
                  <span className={styles.fieldError} role="alert">
                    {errors.place}
                  </span>
                ) : null}
              </ChoiceField>
            )
          ) : (
            <>
              {o && o.targets.length === 0 ? (
                <Alert tone="warning" title="No delivery can take this load">
                  No other client's delivery of the same items and quantity is waiting to be planned.
                </Alert>
              ) : (
                <RadioCards
                  layout="list"
                  aria-label="Delivery that takes the load"
                  value={targetId}
                  onValueChange={setTargetId}
                  options={(o?.targets ?? []).map((t) => ({
                    value: t.id,
                    label: `${t.delivery_number} · ${t.business ?? t.customer}`,
                    description: [
                      t.place,
                      t.zone,
                      t.distance_km === null
                        ? null
                        : `${t.distance_km} km ${o?.distance_from === "truck" ? "from the truck" : "away"}`,
                    ]
                      .filter(Boolean)
                      .join(" · "),
                  }))}
                />
              )}
              {o && o.targets.length > 0 && check.show(errors.target) ? (
                <span className={styles.fieldError} role="alert">
                  {errors.target}
                </span>
              ) : null}
              {o && o.other_waiting > 0 ? (
                <p className={styles.muted}>
                  {o.other_waiting === 1
                    ? "1 more delivery is waiting but carries a different load."
                    : `${o.other_waiting} more deliveries are waiting but carry a different load.`}
                </p>
              ) : null}
              <ChoiceField label={`And ${delivery.order.customer_name}'s delivery?`}>
                <RadioCards
                  layout="list"
                  aria-label={`And ${delivery.order.customer_name}'s delivery?`}
                  value={firstClient}
                  onValueChange={(v) => setFirstClient(v as "deliver_later" | "cancel")}
                  options={[
                    {
                      value: "deliver_later",
                      label: "Deliver to them later",
                      description: "A new delivery to the same place waits under Unassigned",
                    },
                    { value: "cancel", label: "Cancel it", description: "Their order no longer owes this delivery" },
                  ]}
                />
              </ChoiceField>
            </>
          )}
          <ReasonFields picker={reason} showMissing={check.tried} rows={2} />
          {o && (o.fee !== null || o.pay !== null) ? (
            <Alert tone="info" title={`Reroute fee ${formatMoney(o.fee ?? "0", format)}`}>
              {[
                `Charged to ${delivery.order.customer_name}.`,
                o.pay === null
                  ? null
                  : o.payee
                    ? `${formatMoney(o.pay, format)} is paid to ${o.payee.name}, the truck's owner.`
                    : "The truck is the company's own: nobody is paid.",
              ]
                .filter(Boolean)
                .join(" ")}
            </Alert>
          ) : null}
          {mutation.error ? (
            <Alert tone="danger" title="Not rerouted">
              {messageFor(errorOf(mutation.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              disabled={noChoice}
              loading={mutation.isPending}
              onClick={() => check.submit(errors, () => mutation.mutate())}
            >
              {mode === "place" ? "Reroute" : "Send the load"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
